// The CMS-1500 claim form (brief 1E-2, PLAN.md 4.10 and 8.5; ported from FlowCare Ops PRs #61 and
// #62, docs/reference/cms1500/from-flowcare-ops/cms1500.js, with its fixes). FlowCare fills the
// official NUCC 1500 (02/12) fillable template (docs/reference/cms1500/cms1500-02-12-fillable.pdf,
// 2 pages, 252 fields) field by field and never draws the form itself (server/core/cms1500-pdf.ts).
//
// Pure functions: the template's fields (which box each is, which stay blank, its checkbox states
// and lengths), each form's values from a claim's facts, line grouping, the -A/-B suffixes, the
// office's edits, the money check and the holds. What a payer wants is data, never code: where
// every box comes from (`box_sources`), the fixed boxes, the service codes, how lines group, the
// packet. They live on the client's claim setup (claim_setups), started from a template
// (claim_setup_templates, Workers' comp seeded from Thomas's box guide). Only the boxes that come
// from the business, the rate rows and the invoice (24J, 25, 26, 28, 33a) are fixed here: they
// can't be typed over or pointed elsewhere.

import { businessFor, type BusinessSettings } from "./businesses";

export const CMS1500_TEMPLATE_FIELD_COUNT = 252;
/** The template, byte for byte (the build checks docs/reference against it). */
export const CMS1500_TEMPLATE_SHA256 =
  "4fdf27c106d1cafdbdb1c705704b8d2dff122df29146bbc04064ad8244c58884";
export const CMS1500_LINES_PER_FORM = 6;

const LINE_NUMBERS = [1, 2, 3, 4, 5, 6] as const;
const perLine = <T>(make: (n: number) => T[]): T[] => LINE_NUMBERS.flatMap(make);

// ---- The template's fields ------------------------------------------------------------------------

/**
 * Every template field FlowCare fills, with its box. A service line's fields are named with its
 * number (sv1_mm_from ... sv6_mm_from); a setup names them once with "#" (sv#_mm_from).
 */
export const CMS1500_FIELD_BOXES: readonly (readonly [string, string])[] = [
  ["insurance_name", "Carrier"],
  ["insurance_address", "Carrier"],
  ["insurance_address2", "Carrier"],
  ["insurance_city_state_zip", "Carrier"],
  ["insurance_type", "1"],
  ["insurance_id", "1a"],
  ["pt_name", "2"],
  ["birth_mm", "3"],
  ["birth_dd", "3"],
  ["birth_yy", "3"],
  ["sex", "3"],
  ["ins_name", "4"],
  ["pt_street", "5"],
  ["pt_city", "5"],
  ["pt_state", "5"],
  ["pt_zip", "5"],
  ["pt_AreaCode", "5"],
  ["pt_phone", "5"],
  ["rel_to_ins", "6"],
  ["ins_street", "7"],
  ["ins_city", "7"],
  ["ins_state", "7"],
  ["ins_zip", "7"],
  ["employment", "10a"],
  ["pt_auto_accident", "10b"],
  ["other_accident", "10c"],
  ["ins_plan_name", "11c"],
  ["ins_benefit_plan", "11d"],
  ["pt_signature", "12"],
  ["ins_signature", "13"],
  ["cur_ill_mm", "14"],
  ["cur_ill_dd", "14"],
  ["cur_ill_yy", "14"],
  ["73", "14"],
  ["lab", "20"],
  ["99icd", "21"],
  ...Array.from({ length: 12 }, (_, i) => [`diagnosis${i + 1}`, "21"] as const),
  ["medicaid_resub", "22"],
  ["original_ref", "22"],
  ["prior_auth", "23"],
  ...perLine((n) =>
    (
      [
        [`sv${n}_mm_from`, "24A"],
        [`sv${n}_dd_from`, "24A"],
        [`sv${n}_yy_from`, "24A"],
        [`sv${n}_mm_end`, "24A"],
        [`sv${n}_dd_end`, "24A"],
        [`sv${n}_yy_end`, "24A"],
        [`place${n}`, "24B"],
        [`cpt${n}`, "24D"],
        [`mod${n}`, "24D"],
        [`mod${n}a`, "24D"],
        [`mod${n}b`, "24D"],
        [`mod${n}c`, "24D"],
        [`diag${n}`, "24E"],
        [`ch${n}`, "24F"],
        [`day${n}`, "24G"],
        [`local${n}`, "24J"],
      ] as const
    ).map(([f, b]) => [f, b] as const),
  ),
  ["tax_id", "25"],
  ["ssn", "25"],
  ["pt_account", "26"],
  ["assignment", "27"],
  ["t_charge", "28"],
  ["amt_paid", "29"],
  ["physician_signature", "31"],
  ["physician_date", "31"],
  ["fac_name", "32"],
  ["fac_street", "32"],
  ["fac_location", "32"],
  ["doc_name", "33"],
  ["doc_street", "33"],
  ["doc_location", "33"],
  ["doc_phone area", "33"],
  ["doc_phone", "33"],
  ["pin", "33a"],
];
export const CMS1500_MAPPED: ReadonlySet<string> = new Set(CMS1500_FIELD_BOXES.map(([f]) => f));
const BOX_OF = new Map(CMS1500_FIELD_BOXES);
/** The box a template field is ("24D", "Carrier"), or "" for one FlowCare keeps blank. */
export const cms1500BoxOf = (field: string) => BOX_OF.get(field) ?? "";

/**
 * Every other template field stays blank, on purpose (Thomas, 2026-10-16: no license numbers,
 * 32a/32b and 33b blank, the shaded supplemental rows blank; "276" is a stray checkbox beside box
 * 28 and "Clear Form" an on-screen button, removed from every claim).
 */
export const CMS1500_ALWAYS_BLANK: readonly string[] = [
  "NUCC USE",
  "40",
  "41",
  "50",
  "57",
  "58",
  "74",
  "85",
  "96",
  "135",
  "157",
  "179",
  "201",
  "223",
  "245",
  "276",
  "Clear Form",
  "ins_phone area",
  "ins_phone",
  "ins_policy",
  "ins_dob_mm",
  "ins_dob_dd",
  "ins_dob_yy",
  "ins_sex",
  "other_ins_name",
  "other_ins_policy",
  "other_ins_plan_name",
  "accident_place",
  "pt_date",
  "ref_physician",
  "id_physician",
  "physician number 17a1",
  "physician number 17a",
  "sim_ill_mm",
  "sim_ill_dd",
  "sim_ill_yy",
  "work_mm_from",
  "work_dd_from",
  "work_yy_from",
  "work_mm_end",
  "work_dd_end",
  "work_yy_end",
  "hosp_mm_from",
  "hosp_dd_from",
  "hosp_yy_from",
  "hosp_mm_end",
  "hosp_dd_end",
  "hosp_yy_end",
  "charge",
  ...perLine((n) => [`emg${n}`, `type${n}`, `plan${n}`, `epsdt${n}`, `local${n}a`]),
  "Suppl",
  "Suppla",
  "Supplb",
  "Supplc",
  "Suppld",
  "Supple",
  "pin1",
  "grp1",
  "grp",
];

/**
 * The checkbox groups FlowCare sets and every appearance state the template's widgets have (the
 * template's export values don't match them, so a box is ticked by its widget's own state; a
 * test checks this list against the template). A setup's value for one of these is one state.
 */
export const CMS1500_STATES: Readonly<Record<string, readonly string[]>> = {
  insurance_type: ["Medicare", "Medicaid", "Tricare", "Champva", "Group", "Feca", "Other"],
  sex: ["M", "F"],
  rel_to_ins: ["S", "M", "C", "O"],
  employment: ["YES", "NO"],
  pt_auto_accident: ["YES", "NO"],
  other_accident: ["YES", "NO"],
  ins_benefit_plan: ["YES", "NO"],
  lab: ["YES", "NO"],
  ssn: ["SSN", "EIN"],
  assignment: ["YES", "NO"],
};
export const isCheckbox = (field: string) => Object.hasOwn(CMS1500_STATES, field);

/** Box 28 is worked out from the form's own lines: shown, never typed in. */
export const CMS1500_COMPUTED: ReadonlySet<string> = new Set(["t_charge"]);
/**
 * Boxes that come only from the business, the rate rows and the invoice, never typed on a claim
 * (Thomas, 2026-10-16; review S4 of PR #61): 24J, 25, 26 and 33a (and 28, computed); and box 22,
 * which only a corrected claim or a void fills (brief 1E-5).
 */
export const CMS1500_LOCKED: ReadonlySet<string> = new Set([
  "medicaid_resub",
  "original_ref",
  "pin",
  "tax_id",
  "ssn",
  "pt_account",
  ...LINE_NUMBERS.map((n) => `local${n}`),
]);

/** The service line a box belongs to (1–6), or 0 for a box of the whole form. */
export function cms1500LineOf(field: string): number {
  const m =
    /^(?:sv([1-6])_(?:mm|dd|yy)_(?:from|end)|place([1-6])|cpt([1-6])|mod([1-6])[abc]?|diag([1-6])|ch([1-6])|day([1-6])|local([1-6]))$/.exec(
      field,
    );
  return m ? Number(m.slice(1).find(Boolean)) : 0;
}
/** A line field's name in a setup, with "#" for its line: sv3_mm_from → sv#_mm_from. */
export const lineKey = (field: string) =>
  cms1500LineOf(field) ? field.replace(/[1-6](?=[_a-z]*$|$)/, "#") : field;

/**
 * Boxes of one form only (fix round 3 of PR #61, C5): 23 (its own certificate when grouped), 26
 * (-A, -B), 28 and 29, and the service lines. Every other box repeats on every form.
 */
export const CMS1500_PER_FORM: ReadonlySet<string> = new Set([
  "prior_auth",
  "pt_account",
  "t_charge",
  "amt_paid",
]);

/**
 * The template's text boxes that hold a set number of characters (its /MaxLen), for checking an
 * edit before it's saved without loading the PDF; a test checks it against the template.
 */
export const CMS1500_MAX_LENGTHS: Readonly<Record<string, number>> = Object.freeze({
  "50": 19,
  "57": 28,
  "58": 28,
  "96": 71,
  birth_mm: 2,
  birth_dd: 2,
  pt_state: 3,
  pt_AreaCode: 3,
  ins_state: 4,
  "ins_phone area": 3,
  ins_dob_mm: 2,
  ins_dob_dd: 2,
  ins_dob_yy: 4,
  sim_ill_dd: 2,
  work_mm_from: 2,
  work_dd_from: 2,
  work_yy_from: 4,
  work_mm_end: 2,
  work_dd_end: 2,
  work_yy_end: 4,
  hosp_mm_from: 2,
  hosp_dd_from: 2,
  hosp_yy_from: 4,
  hosp_mm_end: 2,
  hosp_dd_end: 2,
  hosp_yy_end: 4,
  sv1_dd_from: 2,
  sv1_yy_from: 2,
  sv1_mm_end: 2,
  sv1_dd_end: 2,
  sv1_yy_end: 2,
  sv2_mm_from: 2,
  sv3_mm_from: 2,
  sv4_mm_from: 2,
  sv5_mm_from: 2,
  sv6_mm_from: 2,
  sv2_dd_from: 2,
  sv3_dd_from: 2,
  sv4_dd_from: 2,
  sv5_dd_from: 2,
  sv6_dd_from: 2,
  sv2_yy_from: 2,
  sv3_yy_from: 2,
  sv4_yy_from: 2,
  sv5_yy_from: 2,
  sv6_yy_from: 2,
  sv2_mm_end: 2,
  sv3_mm_end: 2,
  sv4_mm_end: 2,
  sv5_mm_end: 2,
  sv6_mm_end: 2,
  sv2_dd_end: 2,
  sv3_dd_end: 2,
  sv4_dd_end: 2,
  sv5_dd_end: 2,
  sv2_yy_end: 2,
  sv3_yy_end: 2,
  sv4_yy_end: 2,
  sv5_yy_end: 2,
  place2: 3,
  place3: 3,
  place4: 3,
  place5: 3,
  place6: 3,
  "doc_phone area": 3,
  accident_place: 3,
  place1: 3,
});

// ---- Small helpers -----------------------------------------------------------------------------------

const text = (value: unknown) => (value == null ? "" : String(value)).trim();
const isDay = (value: unknown): value is string =>
  typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value);
const digits = (value: unknown) => text(value).replace(/\D/g, "");
export const money2 = (cents: number) => (Number(cents) / 100).toFixed(2);
const unitsText = (value: number) => String(Math.round(Number(value) * 100) / 100);
const centsOf = (value: unknown) =>
  Math.round(Number(String(value ?? "").replace(/[$,\s]/g, "")) * 100);

/** "LAST, FIRST" in capitals; with a middle initial, "LAST, FIRST, M" (box 2, brief 1E-28). */
export function cmsName(first: string, last: string, middle = ""): string {
  return [text(last), text(first), text(middle)].filter(Boolean).join(", ").toUpperCase();
}

/**
 * The codes box 21 prints for one client's claim: the picked diagnoses that are still on the
 * patient, or every one of them until a pick is made. The primary comes first.
 */
export function box21Codes(
  diagnoses: readonly { id: string; code: string; is_primary: boolean; archived_at: unknown }[],
  pick: readonly string[] | null,
): string[] {
  return diagnoses
    .filter((d) => !d.archived_at && (pick === null || pick.includes(d.id)))
    .sort((a, b) => Number(b.is_primary) - Number(a.is_primary))
    .map((d) => d.code)
    .slice(0, 12);
}

/** A US phone as boxes 5 and 33 print it: the area code apart, then the number. */
export function phoneParts(value: unknown): { area: string; number: string } {
  let d = digits(value);
  if (d.length === 11 && d.startsWith("1")) d = d.slice(1);
  if (d.length === 10) return { area: d.slice(0, 3), number: d.slice(3) };
  if (d.length === 7) return { area: "", number: d };
  return { area: "", number: "" };
}
const dashed = (seven: string) =>
  seven.length === 7 ? `${seven.slice(0, 3)}-${seven.slice(3)}` : seven;

export interface AddressParts {
  street: string;
  city: string;
  state: string;
  zip: string;
}

/**
 * One part of an address without the commas, semicolons and spaces typed at either end, so a city
 * typed "Testville," prints "Testville" in box 5 and "Testville, MD" in box 32 (PR #61, S6).
 */
export const addressPart = (value: unknown) => text(value).replace(/^[\s,;]+|[\s,;]+$/g, "");
const US_ADDRESS = /^(.+?),\s*([^,]+?),?\s+([A-Za-z]{2})\.?,?\s+(\d{5}(?:-\d{4})?)\s*$/;
/**
 * "street, city, ST zip" (or "street, city ST zip"), the way an address typed as one line reads.
 * Anything that doesn't read that way comes back unparsed, for the office to enter again.
 */
export function parseUsAddress(value: unknown): AddressParts & { parsed: boolean; raw: string } {
  const raw = text(value)
    .replace(/\s*\n\s*/g, ", ")
    .replace(/\s+/g, " ");
  const m = raw ? US_ADDRESS.exec(raw) : null;
  if (!m) return { street: "", city: "", state: "", zip: "", parsed: false, raw };
  return {
    street: addressPart(m[1]),
    city: addressPart(m[2]),
    state: (m[3] as string).toUpperCase(),
    zip: m[4] as string,
    parsed: true,
    raw,
  };
}
/** An address as parts: its own parts when it has them, else read from one line of text. */
export function addressParts(value: unknown): AddressParts {
  if (value && typeof value === "object") {
    const a = value as Record<string, unknown>;
    return {
      street: addressPart(a.street),
      city: addressPart(a.city),
      state: addressPart(a.state).toUpperCase(),
      zip: addressPart(a.zip),
    };
  }
  const r = parseUsAddress(value);
  return { street: r.street, city: r.city, state: r.state, zip: r.zip };
}
const cityLine = (a: AddressParts) =>
  [[a.city, a.state].filter(Boolean).join(", "), a.zip].filter(Boolean).join(" ");

/**
 * The form's fonts write Latin-1 (WinAnsi): typographic quotes and dashes become their plain
 * forms; anything else would stop the PDF being made, so an edit with it is refused and a value
 * FlowCare fills prints "?".
 */
const PLAIN: Record<string, string> = {
  "\u2018": "'",
  "\u2019": "'",
  "\u201c": '"',
  "\u201d": '"',
  "\u2013": "-",
  "\u2014": "-",
  "\u2026": "...",
  "\u00a0": " ",
};
const plain = (v: string) =>
  v
    .normalize("NFC")
    .replace(/[\u2018\u2019\u201c\u201d\u2013\u2014\u2026\u00a0]/g, (c) => PLAIN[c] ?? c);
export const pdfText = (value: unknown) =>
  plain(text(value)).replace(/[^\x20-\x7e\u00a1-\u00ff]/g, "?");

// ---- Box sources (data) ------------------------------------------------------------------------------

/** How a value is written into its box. */
export const FORMATS = [
  "mm",
  "dd",
  "yyyy",
  "yy",
  "us_date",
  // MM/DD/YY: box 31's date (brief 1E-3).
  "us_date_yy",
  "upper",
  "digits",
  "nodot",
  "last_first",
  "street",
  "city",
  "state",
  "zip",
  "city_line",
  "phone_area",
  "phone_dashed",
  "phone7",
] as const;
export type Format = (typeof FORMATS)[number];

/**
 * Where one box comes from, as a setup stores it:
 * - {const}: the same on every claim ("Other", "YES", "Patient Home");
 * - {from}: a value of the claim, by its path ("patient.birth_date", "payer.claim_number",
 *   "setup.carrier.0", "business.legal_name", "diagnoses.0"), written as `as` says;
 * - {line}: a value of the box's service line (day, pos, hcpcs, modifier, pointer, charge, units,
 *   npi), blank on an empty line;
 * - {form}: this form's own value (account, total, certificate).
 * `when` leaves the box blank unless that path has a value; `prefix` goes before a value that's
 * there ("Contract # - ").
 */
export interface BoxSource {
  const?: string;
  from?: string;
  line?: "day" | "pos" | "hcpcs" | "modifier" | "charge" | "units" | "npi" | "pointer";
  /** Which modifier (0 to 3). */
  n?: number;
  form?: "account" | "total" | "certificate";
  as?: Format;
  when?: string;
  prefix?: string;
}
export type BoxSources = Record<string, BoxSource>;
const LINE_PARTS = [
  "day",
  "pos",
  "hcpcs",
  "modifier",
  "charge",
  "units",
  "npi",
  "pointer",
] as const;

/**
 * The boxes no setup can point elsewhere (24J, 25, 26, 28, 33a): only from the business, the rate
 * rows and the invoice. A setup's own sources for them are ignored and refused when saved.
 */
export const LOCKED_SOURCES: Readonly<BoxSources> = {
  "local#": { line: "npi" },
  tax_id: { from: "business.ein", as: "digits" },
  ssn: { const: "EIN" },
  pt_account: { form: "account" },
  t_charge: { form: "total" },
  pin: { from: "business.npi", as: "digits" },
};

/** Reads a setup's box sources: every box a mapped field, every source well formed. */
export function readBoxSources(
  input: unknown,
): { ok: true; value: BoxSources } | { ok: false; error: string } {
  if (!input || typeof input !== "object" || Array.isArray(input))
    return { ok: false, error: "Box sources are a list of boxes." };
  const out: BoxSources = {};
  for (const [field, raw] of Object.entries(input as Record<string, unknown>)) {
    const real = field.replace("#", "1");
    if (!CMS1500_MAPPED.has(real)) return { ok: false, error: `${field} isn't a box of the form.` };
    if (
      Object.hasOwn(LOCKED_SOURCES, field) ||
      CMS1500_LOCKED.has(real) ||
      CMS1500_COMPUTED.has(real)
    )
      return {
        ok: false,
        error: `Box ${cms1500BoxOf(real)} comes only from the business, the rate rows and the invoice.`,
      };
    if (!raw || typeof raw !== "object" || Array.isArray(raw))
      return { ok: false, error: `Say where ${field} comes from.` };
    const s = raw as Record<string, unknown>;
    // One place: a value of the claim, of its line or of its form, or else a fixed value (a
    // line's pointer is fixed text on a line).
    const kinds = ["from", "line", "form"].filter((k) => s[k] !== undefined);
    if (kinds.length > 1 || (!kinds.length && typeof s.const !== "string"))
      return { ok: false, error: `${field} comes from one place.` };
    if (s.line !== undefined && !LINE_PARTS.includes(s.line as never))
      return { ok: false, error: `${field}: ${String(s.line)} isn't part of a line.` };
    if (s.form !== undefined && !["account", "total", "certificate"].includes(String(s.form)))
      return { ok: false, error: `${field}: ${String(s.form)} isn't a value of the form.` };
    if (s.as !== undefined && !(FORMATS as readonly string[]).includes(String(s.as)))
      return { ok: false, error: `${field}: ${String(s.as)} isn't a way to write a box.` };
    for (const k of ["const", "from", "when", "prefix"])
      if (s[k] !== undefined && (typeof s[k] !== "string" || (s[k] as string).length > 200))
        return { ok: false, error: `${field}: ${k} is text.` };
    if (
      isCheckbox(real) &&
      s.const !== undefined &&
      !(CMS1500_STATES[real] ?? []).includes(s.const as string)
    )
      return { ok: false, error: `${field} is a checkbox: ${CMS1500_STATES[real]?.join(", ")}.` };
    out[field] = Object.fromEntries(
      Object.entries(s).filter(([k]) =>
        ["const", "from", "line", "n", "form", "as", "when", "prefix"].includes(k),
      ),
    ) as BoxSource;
  }
  return { ok: true, value: out };
}

/** The value at a dotted path ("patient.address", "setup.carrier.0"). */
function at(facts: unknown, path: string): unknown {
  let v: unknown = facts;
  for (const k of path.split(".")) {
    if (v == null || typeof v !== "object") return undefined;
    v = (v as Record<string, unknown>)[k];
  }
  return v;
}
const present = (v: unknown) =>
  v !== undefined && v !== null && v !== false && (typeof v === "object" || text(v) !== "");

function format(v: unknown, as: Format | undefined): string {
  if (!as) return v && typeof v === "object" ? "" : text(v);
  const day = isDay(v) ? v : "";
  switch (as) {
    case "mm":
      return day.slice(5, 7);
    case "dd":
      return day.slice(8, 10);
    case "yyyy":
      return day.slice(0, 4);
    case "yy":
      return day.slice(2, 4);
    case "us_date":
      return day ? `${day.slice(5, 7)}/${day.slice(8, 10)}/${day.slice(0, 4)}` : "";
    case "us_date_yy":
      return day ? `${day.slice(5, 7)}/${day.slice(8, 10)}/${day.slice(2, 4)}` : "";
    case "upper":
      return text(v).toUpperCase();
    case "digits":
      return digits(v);
    case "nodot":
      return text(v).replace(/\./g, "");
    case "last_first": {
      const n = (v && typeof v === "object" ? v : {}) as Record<string, unknown>;
      return cmsName(text(n.first), text(n.last), text(n.middle));
    }
    case "street":
    case "city":
    case "state":
    case "zip":
      return addressParts(v)[as];
    case "city_line":
      return cityLine(addressParts(v));
    case "phone_area":
      return phoneParts(v).area;
    case "phone_dashed":
      return dashed(phoneParts(v).number);
    case "phone7":
      return phoneParts(v).number;
  }
}

// ---- The claim's facts and its lines -------------------------------------------------------------------

/** The fixed boxes and settings a client's setup holds (brief 1E-2, mockup "Fixed boxes"). */
export interface ClaimFixed {
  /** The carrier block, top right: 4 lines. */
  carrier: string[];
  /** Box 1's tick: one of insurance_type's states. */
  box1: string;
  /** Box 11c. */
  plan: string;
  /** Box 23: the authorization certificate number, on or off. */
  box23: boolean;
  /** Boxes 12, 13 and 31. */
  signature: string;
  /** Box 26: the account number's prefix and its pattern ({prefix}, {invoice}, {week_end}). */
  prefix: string;
  account: string;
  /** The template's instructions page after each form, so a duplex print lines up. */
  instructions: boolean;
}

/** How lines group on this client's claims, and how many fit on a form. */
export interface LineRules {
  /**
   * One line per date of service and service, per visit (invoice line), per day (transport; one
   * unit a day), or per week of a service (brief 1E-35: from the week's first day).
   */
  group: "date" | "visit" | "day" | "week";
  perForm: number;
  /** The first day of a day's billing week, for week lines. */
  weekStart?: (day: string) => string;
}

/** One service's codes on this client's claims (the setup's Service codes). */
export interface ServiceCode {
  hcpcs: string;
  modifiers: string;
  pos: string;
  /** Whose NPI fills 24J: the business's rendering NPI or its billing NPI. */
  npi: "rendering" | "billing";
  /** A service with its own grouping (transport: one line per day). */
  group?: LineRules["group"];
  /** Brief 1E-16: a line's own rendering provider's NPI (24J), when the setup's line names one. */
  npiNumber?: string;
}

/** One invoice line of the patient, as the claim reads it. */
export interface ClaimLineIn {
  id: string;
  day: string;
  serviceId: string;
  units: number;
  amountCents: number;
  /** The authorization certificate number covering it. */
  certificate: string;
}

/** Everything a claim is filled from: the facts the box sources point into. */
/**
 * Box 22 (brief 1E-5; DECISIONS.md 10/25): a corrected claim, made when a client rebills the whole
 * line, is code 7 (replacement of a prior claim); a claim already sent and then voided can be
 * resent as code 8 (void or cancel the prior claim). Brief 1E-13 (PR #39 review): the original
 * reference is the payer's own claim number for the prior claim, saved when its payment was
 * posted; without one the claim is held.
 */
export interface Resubmission {
  code: "7" | "8";
  /** The payer's claim number of the claim it replaces or voids; "" while it's missing. */
  original: string;
}

/** The hold a corrected or void claim has while the payer's claim number isn't on file. */
export const PAYER_NUMBER_MISSING = "Payer claim number missing";

/**
 * The box 22 a claim carries: code 7 when every line on it rebills a line as a corrected claim
 * (the client rebills the whole line); otherwise none. The original reference is the payer's
 * claim number of the claim of the line it rebills ("" when none is on file, which holds it).
 */
export function resubmissionOf(input: {
  rebillWhole: boolean;
  lines: readonly { rebillOf: string | null }[];
  payerNumbers: readonly (string | null)[];
}): Resubmission | null {
  if (!input.rebillWhole || !input.lines.length) return null;
  if (!input.lines.every((l) => l.rebillOf)) return null;
  const original = [...new Set(input.payerNumbers.map((n) => text(n)).filter(Boolean))][0];
  return { code: "7", original: original ?? "" };
}

/** Why box 22 holds the claim (the payer's claim number missing), or null. */
export const box22Hold = (r: Resubmission | null | undefined): string | null =>
  r && !text(r.original) ? PAYER_NUMBER_MISSING : null;

/** Code 8 for a voided claim already sent: the same claim, with box 22 filled. */
export function voidResubmission(
  values: Readonly<Record<string, string>>,
  payerNumber: string,
): Record<string, string> {
  return { ...values, medicaid_resub: "8", original_ref: payerNumber };
}

/**
 * The payer's claim number as typed (from a remittance or by the office): up to 30 letters,
 * numbers and dashes, in capitals; empty is none.
 */
export function readPayerClaimNumber(
  v: unknown,
): { ok: true; value: string | null } | { ok: false; error: string } {
  const n = typeof v === "string" ? v.trim().toUpperCase().replace(/\s+/g, "") : "";
  if (!n) return { ok: true, value: null };
  if (n.length > 30 || !/^[A-Z0-9-]+$/.test(n))
    return { ok: false, error: "A payer claim number is up to 30 letters, numbers and dashes." };
  return { ok: true, value: n };
}

/** The latest of a claim's payer claim numbers (one invoice and patient), or null. */
export function payerNumberOf<
  R extends { invoice_id: string; patient_id: string; number: string; created_at: Date },
>(rows: readonly R[], invoiceId: string, patientId: string): R | null {
  return (
    [...rows]
      .filter((r) => r.invoice_id === invoiceId && r.patient_id === patientId)
      .sort((a, b) => b.created_at.getTime() - a.created_at.getTime())[0] ?? null
  );
}

/** Why a voided claim can't be resent as code 8, or null when it can (Billing at Edit, a reason). */
export function refuseVoidResend(input: {
  claimStatus: "draft" | "approved" | "void";
  wasApproved: boolean;
  invoiceSent: boolean;
  alreadyResent: boolean;
  billingEdit: boolean;
  reason: string;
  /** The payer's claim number of the claim (brief 1E-13). */
  payerNumber: string | null;
}): string | null {
  if (!input.billingEdit) return "Resending a void needs Billing at Edit.";
  if (input.claimStatus !== "void") return "Only a voided claim is resent as a void.";
  if (!input.wasApproved || !input.invoiceSent)
    return "This claim was never sent, so there's nothing to void with the payer.";
  if (input.alreadyResent) return "This void was already sent.";
  if (!text(input.payerNumber)) return PAYER_NUMBER_MISSING;
  if (!input.reason.trim()) return "Say why the claim is voided with the payer.";
  if (input.reason.trim().length > 300) return "Keep the reason under 300 characters.";
  return null;
}

export interface ClaimFacts {
  invoice: { number: string; period_to: string; date: string; reissue?: number };
  /** Box 22, on a corrected claim (code 7). */
  resubmission?: Resubmission | null;
  client: { name: string };
  contract: { number: string };
  setup: ClaimFixed;
  patient: Record<string, unknown>;
  payer: Record<string, unknown>;
  diagnoses: string[];
  /** The billing business (boxes 25, 33, 33a; 24J on a line whose service bills under it). */
  business: Record<string, unknown>;
  /** The business that gives the care (24J); the billing business when none is picked. */
  giving?: Record<string, unknown>;
}

/** One service line of a form (box 24). */
export interface ServiceRow {
  day: string;
  serviceId: string;
  hcpcs: string;
  modifiers: string;
  pos: string;
  npiSource: "rendering" | "billing";
  /** Brief 1E-16: the NPI of the rendering provider the setup's line names, when it's another. */
  npiNumber?: string;
  units: number;
  chargeCents: number;
  certificate: string;
  /** The invoice lines it comes from. */
  lineIds: string[];
}

/**
 * The patient's service lines, grouped as the setup says (Thomas, 2026-10-17): one per date of
 * service and service (its units and charges added), one per invoice line, or one per day (a
 * day's transport is one line of 1 unit, its charges added). A service code's own grouping wins.
 * In date order, then by code.
 */
export function serviceRows(
  lines: readonly ClaimLineIn[],
  codes: Readonly<Record<string, ServiceCode>>,
  rules: LineRules,
): ServiceRow[] {
  const rows = new Map<string, ServiceRow>();
  for (const l of lines) {
    const c = codes[l.serviceId] ?? { hcpcs: "", modifiers: "", pos: "12", npi: "rendering" };
    const group = c.group ?? rules.group;
    const week = group === "week" ? (rules.weekStart?.(l.day) ?? l.day) : null;
    const key =
      group === "visit"
        ? `${l.day}|line|${l.id}`
        : week !== null
          ? `${week}|week|${l.serviceId}|${l.certificate}`
          : group === "day"
            ? `${l.day}|day|${c.hcpcs}|${c.modifiers}|${c.pos}|${c.npi}|${c.npiNumber ?? ""}|${l.certificate}`
            : `${l.day}|date|${l.serviceId}|${l.certificate}`;
    let r = rows.get(key);
    if (!r) {
      r = {
        day: l.day,
        serviceId: l.serviceId,
        hcpcs: text(c.hcpcs),
        modifiers: text(c.modifiers),
        pos: text(c.pos) || "12",
        npiSource: c.npi === "billing" ? "billing" : "rendering",
        ...(c.npiNumber !== undefined ? { npiNumber: c.npiNumber } : {}),
        units: 0,
        chargeCents: 0,
        certificate: l.certificate,
        lineIds: [],
      };
      rows.set(key, r);
    }
    // A week line is dated from its first day of service.
    if (l.day < r.day) r.day = l.day;
    r.units = group === "day" ? 1 : Math.round((r.units + Number(l.units)) * 100) / 100;
    r.chargeCents += Number(l.amountCents);
    r.lineIds.push(l.id);
  }
  return [...rows.values()].sort(
    (a, b) => a.day.localeCompare(b.day) || a.hcpcs.localeCompare(b.hcpcs),
  );
}

// ---- Mileage on claims (brief 1E-13) -------------------------------------------------------------------

/** A setup's mileage on claims: off by default; on, with the payer's code for it (24D). */
export interface MileageOnClaims {
  on: boolean;
  code: string;
}

/** Reads the setup's mileage on claims: on or off, and the code (up to 10 letters and numbers). */
export function readMileageOnClaims(
  input: unknown,
  had: MileageOnClaims,
): { ok: true; value: MileageOnClaims } | { ok: false; error: string } {
  const b = (input && typeof input === "object" ? input : {}) as Record<string, unknown>;
  const on = b.on === undefined ? had.on : b.on === true;
  const code =
    b.code === undefined ? had.code : typeof b.code === "string" ? b.code.trim().toUpperCase() : "";
  if (code && !/^[A-Z0-9]{1,10}$/.test(code))
    return { ok: false, error: "The mileage code is up to 10 letters and numbers." };
  return { ok: true, value: { on, code } };
}

/** The code key a mileage line bills under: its own per service, so 24B and 24J follow it. */
export const mileageKey = (serviceId: string) => `mileage:${serviceId}`;

/**
 * The patient's claim lines from their invoice lines. With mileage on claims on, each mileage line
 * (one per visit with miles, at the client's mileage charge, on the visit's date) is a claim line of
 * its own with the setup's code, one line per visit; off, mileage stays on the invoice only.
 * Answers the lines and the codes they bill under.
 */
export function claimLinesOf(
  lines: readonly (ClaimLineIn & { kind: "service" | "mileage" })[],
  mileage: MileageOnClaims,
  codes: Readonly<Record<string, ServiceCode>>,
): { lines: ClaimLineIn[]; codes: Record<string, ServiceCode> } {
  const out: ClaimLineIn[] = [];
  const all: Record<string, ServiceCode> = { ...codes };
  for (const { kind, ...l } of lines) {
    if (kind !== "mileage") {
      out.push(l);
      continue;
    }
    if (!mileage.on) continue;
    const own = codes[l.serviceId];
    all[mileageKey(l.serviceId)] = {
      hcpcs: mileage.code,
      modifiers: "",
      pos: own?.pos ?? "12",
      npi: own?.npi ?? "rendering",
      group: "visit",
    };
    out.push({ ...l, serviceId: mileageKey(l.serviceId) });
  }
  return { lines: out, codes: all };
}

/**
 * The forms: up to perForm lines each. Box 23 holds one number, so with the setup's box 23 on,
 * lines group by certificate first and each form carries one.
 */
export function formPages(rows: readonly ServiceRow[], rules: LineRules, box23: boolean) {
  const per = Math.max(1, Math.min(CMS1500_LINES_PER_FORM, Math.floor(rules.perForm) || 6));
  const chunk = (list: readonly ServiceRow[]) =>
    Array.from({ length: Math.ceil(list.length / per) }, (_, i) =>
      list.slice(i * per, (i + 1) * per),
    );
  if (!rows.length) return [[]] as ServiceRow[][];
  if (!box23) return chunk(rows);
  const groups = new Map<string, ServiceRow[]>();
  for (const r of rows) groups.set(r.certificate, [...(groups.get(r.certificate) ?? []), r]);
  return [...groups.values()].flatMap(chunk);
}

const formLetter = (index: number) =>
  index < 26 ? String.fromCharCode(65 + index) : String(index + 1);
/** The suffix of form n of a claim: "" for a claim of one form, else A, B ... */
export const formSuffix = (formIndex: number, formCount: number) =>
  formCount > 1 ? formLetter(formIndex - 1) : "";

/**
 * Box 26: the setup's pattern ({prefix}-WE{week_end}: CEIWC-WE101726), or the invoice number;
 * a re-issue for the same week adds -02, -03 (PR #62); a claim of more than one form adds -A, -B.
 */
export function cms1500AccountNumber(
  facts: Pick<ClaimFacts, "invoice" | "setup">,
  formIndex: number,
  formCount: number,
): string {
  const end = isDay(facts.invoice.period_to) ? facts.invoice.period_to : "";
  const week = end ? `${end.slice(5, 7)}${end.slice(8, 10)}${end.slice(2, 4)}` : "";
  const pattern = text(facts.setup.account);
  const base =
    pattern && text(facts.setup.prefix)
      ? pattern
          .replace(/\{prefix\}/g, text(facts.setup.prefix))
          .replace(/\{week_end\}/g, week)
          .replace(/\{invoice\}/g, text(facts.invoice.number))
      : text(facts.invoice.number);
  const n = Number(facts.invoice.reissue ?? 1);
  const again = n > 1 ? `${base}-${String(n).padStart(2, "0")}` : base;
  const suffix = formSuffix(formIndex, formCount);
  return suffix ? `${again}-${suffix}` : again;
}

export interface ClaimForm {
  index: number;
  suffix: string;
  rows: ServiceRow[];
  values: Record<string, string>;
  totalCents: number;
}
export interface Claim {
  formCount: number;
  forms: ClaimForm[];
  totalCents: number;
}

/** The setup's source for a field (a line field by its "#" name); the locked boxes' own. */
export function sourceOf(sources: Readonly<BoxSources>, field: string): BoxSource | null {
  const key = lineKey(field);
  return LOCKED_SOURCES[key] ?? sources[key] ?? null;
}

function lineValue(s: BoxSource, row: ServiceRow | undefined, facts: ClaimFacts): string {
  if (!row) return "";
  switch (s.line) {
    case "day":
      return format(row.day, s.as);
    case "pos":
      return row.pos;
    case "hcpcs":
      return row.hcpcs;
    case "modifier":
      return row.modifiers.split(/\s+/).filter(Boolean)[s.n ?? 0] ?? "";
    case "pointer":
      return text(s.const) || "A";
    case "charge":
      return money2(row.chargeCents);
    case "units":
      return unitsText(row.units);
    case "npi":
      return digits(
        row.npiNumber ??
          (row.npiSource === "billing" ? facts.business.npi : (facts.giving ?? facts.business).npi),
      );
    default:
      return "";
  }
}

/**
 * Each form's values, by template field: what FlowCare fills from the setup's sources before any
 * office edit. Every form repeats the top and provider boxes; 23, 26, 28 and the lines are its own.
 */
export function cms1500Claim(
  facts: ClaimFacts,
  lines: readonly ClaimLineIn[],
  setup: {
    sources: Readonly<BoxSources>;
    codes: Readonly<Record<string, ServiceCode>>;
    rules: LineRules;
  },
): Claim {
  const rows = serviceRows(lines, setup.codes, setup.rules);
  const pages = formPages(rows, setup.rules, facts.setup.box23);
  const formCount = pages.length;
  const forms = pages.map((page, i): ClaimForm => {
    const index = i + 1;
    const totalCents = page.reduce((t, r) => t + r.chargeCents, 0);
    const certificates = [...new Set(page.map((r) => r.certificate).filter(Boolean))];
    const own = {
      account: cms1500AccountNumber(facts, index, formCount),
      total: money2(totalCents),
      certificate: facts.setup.box23 ? certificates.join(" ") : "",
    };
    const values: Record<string, string> = {};
    for (const field of CMS1500_MAPPED) {
      const s = sourceOf(setup.sources, field);
      const line = cms1500LineOf(field);
      const row = line ? page[line - 1] : undefined;
      let v = "";
      if (s && (!s.when || (s.when === "line" ? !!row : present(at(facts, s.when))))) {
        if (s.line) v = lineValue(s, row, facts);
        else if (line && !row) v = "";
        else if (s.form) v = own[s.form];
        else if (s.from !== undefined) v = format(at(facts, s.from), s.as);
        else v = text(s.const);
        if (v && s.prefix) v = `${s.prefix}${v}`;
      }
      // A checkbox is ticked by one of its own states, or not at all.
      if (isCheckbox(field)) v = (CMS1500_STATES[field] ?? []).includes(v) ? v : "";
      values[field] = v;
    }
    values.medicaid_resub = facts.resubmission?.code ?? "";
    values.original_ref = facts.resubmission?.original ?? "";
    return { index, suffix: formSuffix(index, formCount), rows: page, values, totalCents };
  });
  return { formCount, forms, totalCents: forms.reduce((t, f) => t + f.totalCents, 0) };
}

// ---- Who bills and who gives the care (brief 1E-14) -------------------------------------------------------

/** A claim setup's two picks; null is the default (the contract's business; same as billing). */
export interface ClaimPicks {
  billing_business_id: string | null;
  rendering_business_id: string | null;
}

/**
 * The business that bills (boxes 25, 33, 33a) and the one that gives the care (24J) on a claim
 * setup's claims: the setup's billing pick when it's live, else the contract's business, else the
 * module's (businessFor); giving care is the setup's pick when it's live and isn't the billing
 * business, else the billing business. A company with one live business uses it for both.
 */
export function claimBusinesses<B extends { id: string }>(
  setup: ClaimPicks | null,
  contractBusiness: string | null | undefined,
  settings: { moduleBusiness: BusinessSettings["moduleBusiness"] },
  businesses: readonly B[],
): { ok: true; billing: B; giving: B; same: boolean } | { ok: false; error: string } {
  const live = businesses.map((b) => b.id);
  const own = (id: string | null | undefined) => businesses.find((b) => b.id === id) ?? null;
  const picked = own(setup?.billing_business_id);
  const fallback = picked
    ? null
    : businessFor("homecare", contractBusiness, { moduleBusiness: settings.moduleBusiness, live });
  if (fallback && !fallback.ok) return fallback;
  const billing = (picked ?? own(fallback?.ok ? fallback.business : null)) as B;
  const giving = own(setup?.rendering_business_id);
  return giving && giving.id !== billing.id
    ? { ok: true, billing, giving, same: false }
    : { ok: true, billing, giving: billing, same: true };
}

/**
 * Reads the setup card's picks: each a live business of the company, or null (the default). A
 * giving-care pick of the billing business is the default too.
 */
export function readClaimPicks(
  input: { billingBusiness?: unknown; renderingBusiness?: unknown },
  had: ClaimPicks,
  live: readonly string[],
): { ok: true; value: ClaimPicks } | { ok: false; error: string } {
  const pick = (v: unknown, was: string | null) => {
    if (v === undefined) return { ok: true as const, value: was };
    if (v === null || v === "" || v === "same") return { ok: true as const, value: null };
    return typeof v === "string" && live.includes(v)
      ? { ok: true as const, value: v }
      : { ok: false as const, error: "Pick one of the company's businesses." };
  };
  const bill = pick(input.billingBusiness, had.billing_business_id);
  if (!bill.ok) return bill;
  const rend = pick(input.renderingBusiness, had.rendering_business_id);
  if (!rend.ok) return rend;
  return {
    ok: true,
    value: {
      billing_business_id: bill.value,
      rendering_business_id: rend.value && rend.value !== bill.value ? rend.value : null,
    },
  };
}

// ---- Office edits ------------------------------------------------------------------------------------

/** Why a box can't be typed in, and where it comes from (fix round 3 of PR #61, C4). */
export function cms1500LockReason(
  field: string,
): { box: string; from: string; text: string } | null {
  if (field === "pin")
    return { box: "33a", from: "business", text: "Box 33a is the billing provider's NPI." };
  if (field === "tax_id" || field === "ssn")
    return {
      box: "25",
      from: "business",
      text: "Box 25 is the billing provider's federal tax ID (EIN).",
    };
  if (field === "pt_account")
    return {
      box: "26",
      from: "invoice",
      text: "Box 26 is the account number from the invoice (with -A, -B on a claim of more than one form).",
    };
  if (/^local[1-6]$/.test(field))
    return {
      box: "24J",
      from: "rate row",
      text: "Box 24J is the rendering provider's NPI, or the billing provider's for a service billed under it.",
    };
  if (field === "t_charge")
    return { box: "28", from: "invoice", text: "Box 28 adds up this form's own line charges." };
  if (field === "medicaid_resub" || field === "original_ref")
    return {
      box: "22",
      from: "invoice",
      text: "Box 22 is filled only on a corrected claim (code 7) or a void (code 8), with the original claim number.",
    };
  if (CMS1500_ALWAYS_BLANK.includes(field))
    return { box: "", from: "", text: "FlowCare keeps this box blank on every claim." };
  return null;
}

/** An edit names its form and field: "p<form>.<field>". Null for a box that can't be edited. */
export function cms1500EditName(
  name: string,
  formCount: number,
): { form: number; field: string } | null {
  const m = /^p([1-9]\d{0,2})\.(.+)$/.exec(String(name || ""));
  if (!m) return null;
  const form = Number(m[1]);
  const field = m[2] as string;
  if (
    form < 1 ||
    form > formCount ||
    !CMS1500_MAPPED.has(field) ||
    CMS1500_COMPUTED.has(field) ||
    CMS1500_LOCKED.has(field)
  )
    return null;
  return { form, field };
}

/**
 * An edit that still has a place on this claim: its form exists, and a line's box sits on a line
 * with a service (re-review S1 of PR #61). One without a place never prints.
 */
export function cms1500EditAt(claim: Claim, name: string) {
  const e = cms1500EditName(name, claim.formCount);
  const line = e ? cms1500LineOf(e.field) : 0;
  return e && (!line || claim.forms[e.form - 1]?.rows[line - 1]) ? e : null;
}

/** Whether a box repeats on every form the same, so an edit can go on every form at once. */
export function cms1500Repeats(claim: Claim, field: string): boolean {
  if (
    cms1500LineOf(field) ||
    CMS1500_PER_FORM.has(field) ||
    !CMS1500_MAPPED.has(field) ||
    CMS1500_COMPUTED.has(field) ||
    CMS1500_LOCKED.has(field)
  )
    return false;
  const first = claim.forms[0]?.values[field] ?? "";
  return claim.forms.every((f) => (f.values[field] ?? "") === first);
}

const CHARGE = /^\d{1,7}(?:\.\d{1,2})?$/;
/** Checks one typed value: the value to keep, or why not. */
export function cms1500EditValue(
  field: string,
  value: unknown,
): { ok: true; value: string } | { ok: false; error: string } {
  if (typeof value !== "string" && typeof value !== "number")
    return { ok: false, error: "Type the box's value as text." };
  let v = String(value)
    .replace(/\p{Cc}/gu, " ")
    .trim();
  const states = CMS1500_STATES[field];
  if (states) {
    if (v === "" || v === "Off") return { ok: true, value: "" };
    if (!states.includes(v))
      return { ok: false, error: `Choose ${states.join(" or ")}, or leave it unticked.` };
    return { ok: true, value: v };
  }
  if (/^ch\d$/.test(field) && v !== "") {
    v = v.replace(/[$,\s]/g, "");
    if (!CHARGE.test(v))
      return { ok: false, error: "Type the charge as an amount, for example 148.00." };
    v = money2(centsOf(v));
  }
  // Box 21 prints without the decimal point, a code typed in place too (re-review N3).
  if (/^diagnosis\d+$/.test(field)) v = v.replace(/\./g, "");
  if (/^day\d$/.test(field) && v !== "" && !/^\d{1,4}(?:\.\d{1,2})?$/.test(v))
    return { ok: false, error: "Type the units as a number, for example 24 or 1.5." };
  if (pdfText(v) !== plain(v))
    return {
      ok: false,
      error:
        "Type this box in plain letters, numbers and punctuation (the form's font can't print some characters).",
    };
  const max = CMS1500_MAX_LENGTHS[field];
  if (max && v.length > max)
    return { ok: false, error: `This box holds at most ${max} characters.` };
  if (v.length > 200) return { ok: false, error: "This box holds at most 200 characters." };
  return { ok: true, value: v };
}

/** One logged correction: the box, what it was and is, who, when and why. */
export interface ClaimEdit {
  form: number;
  field: string;
  from: string;
  to: string;
  by: string;
  at: string;
  reason: string;
}

/** An edit needs a reason, as every correction of a claim does. */
export function readEditReason(
  v: unknown,
): { ok: true; value: string } | { ok: false; error: string } {
  const r = typeof v === "string" ? v.replace(/\s+/g, " ").trim() : "";
  if (!r) return { ok: false, error: "Say why the box is corrected." };
  if (r.length > 300) return { ok: false, error: "The reason is up to 300 characters." };
  return { ok: true, value: r };
}

/**
 * The values each form prints: FlowCare's, then the office's edits (the latest for a box wins).
 * A form whose charges or units were edited gets box 28 worked out again from its own lines.
 */
export function cms1500Effective(
  claim: Claim,
  edits: readonly Pick<ClaimEdit, "form" | "field" | "to">[],
) {
  return claim.forms.map((f) => {
    const values = { ...f.values };
    let moneyEdited = false;
    for (const e of edits) {
      if (e.form !== f.index || !cms1500EditAt(claim, `p${e.form}.${e.field}`)) continue;
      values[e.field] = e.to;
      if (/^(ch|day)\d$/.test(e.field)) moneyEdited = true;
    }
    if (moneyEdited)
      values.t_charge = money2(
        LINE_NUMBERS.reduce((t, n) => t + (values[`ch${n}`] ? centsOf(values[`ch${n}`]) : 0), 0),
      );
    return { index: f.index, values, moneyEdited };
  });
}
export type Effective = ReturnType<typeof cms1500Effective>;

const LINE_FIELDS = (n: number) => [
  `sv${n}_mm_from`,
  `sv${n}_dd_from`,
  `sv${n}_yy_from`,
  `cpt${n}`,
  `ch${n}`,
  `day${n}`,
];
const lineFilled = (values: Record<string, string>, n: number) =>
  LINE_FIELDS(n).some((k) => text(values[k]) !== "");

export interface Problem {
  form: number;
  box: string;
  text: string;
}

/**
 * The money check (block money): each form's lines are the invoice's lines for this patient,
 * charge for charge and unit for unit (money moved between lines or forms is caught too), no line
 * added or removed, and the claim's total is the patient's total on the invoice. A mismatch holds
 * the claim. Text corrections are allowed and logged.
 */
export function cms1500MoneyCheck(
  claim: Claim,
  effective: Effective,
  invoiceNumber: string,
  patientTotalCents: number,
): { ok: boolean; problems: Problem[]; message: string } {
  const problems: Problem[] = [];
  let total = 0;
  for (const e of effective) {
    const f = claim.forms[e.index - 1] as ClaimForm;
    let formTotal = 0;
    for (const n of LINE_NUMBERS) {
      const row = f.rows[n - 1];
      const now = lineFilled(e.values, n);
      if (!!row !== now)
        problems.push({
          form: e.index,
          box: "24",
          text: row ? `line ${n} was removed` : `line ${n} was added`,
        });
      else if (row && unitsText(Number(e.values[`day${n}`] || 0)) !== f.values[`day${n}`])
        problems.push({
          form: e.index,
          box: "24G",
          text: `line ${n} units changed from ${f.values[`day${n}`]} to ${e.values[`day${n}`] || "blank"}`,
        });
      if (row && now && centsOf(e.values[`ch${n}`] || 0) !== row.chargeCents)
        problems.push({
          form: e.index,
          box: "24F",
          text: `line ${n} charge changed from ${money2(row.chargeCents)} to ${e.values[`ch${n}`] || "blank"}`,
        });
      formTotal += centsOf(e.values[`ch${n}`] || 0);
    }
    const box28 = centsOf(e.values.t_charge || 0);
    if (box28 !== formTotal)
      problems.push({
        form: e.index,
        box: "28",
        text: `box 28 is ${money2(box28)}; its lines add up to ${money2(formTotal)}`,
      });
    total += box28;
  }
  if (total !== Number(patientTotalCents))
    problems.push({
      form: 0,
      box: "28",
      text: `the forms total ${money2(total)}; the invoice bills ${money2(patientTotalCents)} for this patient`,
    });
  return {
    ok: !problems.length,
    problems,
    message: problems.length
      ? `The claim no longer matches invoice ${invoiceNumber}. Undo the edit or correct the invoice.`
      : "",
  };
}

/** Where a hold is fixed: the patient, their payer (claim), the client's setup or the business. */
export type FixAt = "patient" | "payer" | "setup" | "business" | "invoice" | "payment";

export interface Hold {
  kind: "hold" | "check" | "info";
  box: string;
  /** "Box 14", or "Carrier block". */
  where: string;
  forms: string;
  text: string;
  fix: FixAt;
}

/** Where a box's value is fixed, from where its source points. */
function fixOf(sources: Readonly<BoxSources>, field: string): FixAt {
  const s = sourceOf(sources, field);
  const from = s?.from ?? "";
  if (s?.line === "npi" || from.startsWith("business.")) return "business";
  if (s?.line) return "setup";
  if (from.startsWith("payer.")) return "payer";
  if (from.startsWith("patient.") || from.startsWith("diagnoses")) return "patient";
  if (s?.form) return "invoice";
  return "setup";
}

/**
 * What holds the claim and what to look at, each naming its box and form, on the values the
 * claim prints (so a box corrected in place clears its own hold). Only these hold (brief 1E-2): a
 * missing claim number (1a), date of birth (3), employer (4), patient address (5), employer
 * address (7), date of injury (14), service code (24D), NPI (24J, 33a). Everything else is a
 * check to look at, never a hold.
 */
export function cms1500Holds(
  claim: Claim,
  effective: Effective,
  sources: Readonly<BoxSources>,
  names: { billing: string; giving: string } = { billing: "", giving: "" },
): Hold[] {
  const out: Hold[] = [];
  const all = effective.length;
  const named = (indexes: number[]) =>
    indexes.length === all
      ? all === 1
        ? "Form 1"
        : "All forms"
      : indexes.length === 1
        ? `Form ${indexes[0]}`
        : `Forms ${indexes.join(", ")}`;
  const each = (
    kind: Hold["kind"],
    box: string,
    test: (v: Record<string, string>) => boolean,
    words: string,
    field: string,
  ) => {
    const forms = effective.filter((e) => test(e.values)).map((e) => e.index);
    if (forms.length)
      out.push({
        kind,
        box,
        where: box === "Carrier" ? "Carrier block" : `Box ${box}`,
        forms: named(forms),
        text: words,
        fix: fixOf(sources, field),
      });
  };
  const blank =
    (...keys: string[]) =>
    (v: Record<string, string>) =>
      keys.some((k) => !text(v[k]));
  const lines = (v: Record<string, string>, bad: (n: number) => boolean) =>
    LINE_NUMBERS.some((n) => lineFilled(v, n) && bad(n));
  each("hold", "1a", blank("insurance_id"), "no claim number", "insurance_id");
  each("hold", "3", blank("birth_mm", "birth_dd", "birth_yy"), "no date of birth", "birth_mm");
  each("hold", "4", blank("ins_name"), "no employer", "ins_name");
  each(
    "hold",
    "5",
    blank("pt_street", "pt_city", "pt_state", "pt_zip"),
    "the patient's street, city, state or ZIP is missing",
    "pt_street",
  );
  each(
    "hold",
    "7",
    blank("ins_street", "ins_city", "ins_state", "ins_zip"),
    "the employer's address is missing",
    "ins_street",
  );
  each(
    "hold",
    "14",
    blank("cur_ill_mm", "cur_ill_dd", "cur_ill_yy"),
    "no date of injury",
    "cur_ill_mm",
  );
  each(
    "hold",
    "24D",
    (v) => lines(v, (n) => !text(v[`cpt${n}`])),
    "a line has no service code",
    "cpt1",
  );
  // Brief 1E-14: a picked business with no NPI holds the claim, naming the box and the business:
  // 24J the business that gives the care (or bills, for a line whose service bills under it), 33a
  // the billing business.
  const noNpi = (who: string, otherwise: string) => (who ? `${who} has no NPI` : otherwise);
  const rowsOf = new Map(effective.map((e) => [e.values, claim.forms[e.index - 1]?.rows ?? []]));
  const missing24J = (v: Record<string, string>, source?: ServiceRow["npiSource"]) =>
    lines(
      v,
      (n) =>
        !/^\d{10}$/.test(text(v[`local${n}`])) &&
        (!source || (rowsOf.get(v)?.[n - 1]?.npiSource ?? "rendering") === source),
    );
  if (names.giving && names.giving !== names.billing) {
    each("hold", "24J", (v) => missing24J(v, "rendering"), noNpi(names.giving, ""), "local1");
    each("hold", "24J", (v) => missing24J(v, "billing"), noNpi(names.billing, ""), "local1");
  } else
    each("hold", "24J", (v) => missing24J(v), noNpi(names.billing, "a line has no NPI"), "local1");
  each(
    "hold",
    "33a",
    (v) => !/^\d{10}$/.test(text(v.pin)),
    noNpi(names.billing, "no billing NPI"),
    "pin",
  );
  each("check", "Carrier", blank("insurance_name"), "no carrier name", "insurance_name");
  each(
    "check",
    "3",
    (v) => !["M", "F"].includes(v.sex ?? ""),
    "the patient's sex isn't recorded; box 3 is left unticked",
    "sex",
  );
  each(
    "check",
    "5",
    (v) => !!text(v.pt_phone) && !text(v.pt_AreaCode),
    "the patient's phone has no area code",
    "pt_phone",
  );
  each("check", "21", blank("diagnosis1"), "no diagnosis on this claim", "diagnosis1");
  each("check", "25", (v) => !/^\d{9}$/.test(text(v.tax_id)), "no federal tax ID (EIN)", "tax_id");
  each(
    "check",
    "33",
    blank("doc_name", "doc_street", "doc_location"),
    "the billing provider's name or address is missing",
    "doc_name",
  );
  each(
    "check",
    "33",
    (v) => !!text(v.doc_phone) && !text(v["doc_phone area"]),
    "the business's phone has no area code",
    "doc_phone",
  );
  if (!claim.forms.some((f) => f.rows.length))
    out.unshift({
      kind: "hold",
      box: "24",
      where: "Box 24",
      forms: "Form 1",
      text: "no lines for this patient",
      fix: "invoice",
    });
  return out;
}

// ---- The setup in plain words ---------------------------------------------------------------------------

/** Each box's name as the form prints it, in the order the setup lists them. */
export const BOX_TITLES: readonly (readonly [string, string])[] = [
  ["Carrier", "Carrier block"],
  ["1", "1 · Type"],
  ["1a", "1a · Insured's ID"],
  ["2", "2 · Patient name"],
  ["3", "3 · Birth date, sex"],
  ["4", "4 · Insured"],
  ["5", "5 · Patient address, phone"],
  ["6", "6 · Relationship"],
  ["7", "7 · Insured's address"],
  ["10a", "10a · Employment related"],
  ["10b", "10b · Auto accident"],
  ["10c", "10c · Other accident"],
  ["11c", "11c · Plan name"],
  ["11d", "11d · Another plan?"],
  ["12", "12 · Patient's signature"],
  ["13", "13 · Insured's signature"],
  ["14", "14 · Date of injury"],
  ["20", "20 · Outside lab"],
  ["21", "21 · Diagnoses"],
  ["23", "23 · Prior authorization"],
  ["24A", "24A · Dates of service"],
  ["24B", "24B · Place of service"],
  ["24D", "24D · HCPCS, modifiers"],
  ["24E", "24E · Pointer"],
  ["24F", "24F · Charges"],
  ["24G", "24G · Units"],
  ["24J", "24J · Rendering NPI"],
  ["25", "25 · Tax ID"],
  ["26", "26 · Account no."],
  ["27", "27 · Accept assignment"],
  ["28", "28 · Total charge"],
  ["29", "29 · Amount paid"],
  ["31", "31 · Signature, date"],
  ["32", "32 · Service facility"],
  ["33", "33 · Billing provider"],
  ["33a", "33a · Billing NPI"],
];

const PATH_WORDS: Record<string, string> = {
  "setup.carrier": "Client setup › carrier block",
  "setup.box1": "Client setup › box 1",
  "setup.plan": "Client setup › plan name",
  "setup.signature": "Client setup › signature text",
  "patient.name": "Patient › LAST, FIRST",
  "patient.birth_date": "Patient › date of birth",
  "patient.sex": "Patient › sex",
  "patient.address": "Patient › address",
  "patient.phone": "Patient › phone",
  diagnoses: "Patient › diagnoses",
  "invoice.date": "Invoice › date",
  "invoice.number": "Invoice › number",
  "client.name": "Client › name",
  "contract.number": "Contract › number",
  "business.legal_name": "Billing provider › legal name",
  "business.name_line": "Billing provider › line under the name",
  "business.address": "Billing provider › address",
  "business.phone": "Billing provider › phone",
  "business.ein": "Billing provider › EIN",
  "business.npi": "Billing provider › NPI",
};
const LINE_WORDS: Record<string, string> = {
  day: "date",
  pos: "place of service",
  hcpcs: "HCPCS",
  modifier: "modifiers",
  pointer: "pointer",
  charge: "charge",
  units: "units",
  npi: "NPI (rendering provider, or the billing provider, as its service code says)",
};

export type SourceKind = "fixed" | "patient" | "visits" | "invoice" | "business";

/** One source in words ("Patient › date of injury"), with what kind of value it is. */
export function sourceWords(
  s: BoxSource,
  payerLabels: Readonly<Record<string, string>> = {},
): { words: string; kind: SourceKind } {
  if (s.line)
    return {
      words: `Approved visits › ${LINE_WORDS[s.line] ?? s.line}${s.line === "pointer" ? ` ${s.const ?? "A"}` : ""}`,
      kind: "visits",
    };
  if (s.form)
    return {
      words:
        s.form === "account"
          ? "Invoice › account number (+ -A, -B for more forms)"
          : s.form === "total"
            ? "Adds up this form's lines"
            : "Authorization certificate number, when box 23 is on",
      kind: "invoice",
    };
  if (s.from !== undefined) {
    const path = s.from;
    const payer = /^payer\.(.+)$/.exec(path)?.[1];
    const base = path.replace(/\.\d+$/, "");
    const words = payer
      ? `Patient › ${payerLabels[payer] ?? payer.replace(/_/g, " ")}`
      : (PATH_WORDS[base] ?? path);
    const kind: SourceKind = path.startsWith("setup.")
      ? "fixed"
      : path.startsWith("business.")
        ? "business"
        : path.startsWith("invoice.")
          ? "invoice"
          : "patient";
    return { words, kind };
  }
  return { words: s.const ?? "", kind: "fixed" };
}

/**
 * "Where every box comes from" (mockup step 4): one row per box, its parts' sources in words,
 * read from the setup's own box_sources (and the locked boxes' fixed ones).
 */
export function describeSources(
  sources: Readonly<BoxSources>,
  payerLabels: Readonly<Record<string, string>> = {},
): { box: string; title: string; from: string; kind: SourceKind }[] {
  const byBox = new Map<string, { words: string[]; kinds: Set<SourceKind> }>();
  for (const field of CMS1500_MAPPED) {
    if (cms1500LineOf(field) > 1) continue;
    const s = sourceOf(sources, field);
    if (!s) continue;
    const box = cms1500BoxOf(field);
    const w = sourceWords(s, payerLabels);
    const at = byBox.get(box) ?? { words: [], kinds: new Set<SourceKind>() };
    if (w.words && !at.words.includes(w.words)) at.words.push(w.words);
    at.kinds.add(w.kind);
    byBox.set(box, at);
  }
  return BOX_TITLES.filter(([box]) => byBox.has(box)).map(([box, title]) => {
    const at = byBox.get(box) as { words: string[]; kinds: Set<SourceKind> };
    const kinds = [...at.kinds];
    const kind = kinds.includes("visits")
      ? "visits"
      : kinds.includes("patient")
        ? "patient"
        : kinds.includes("invoice")
          ? "invoice"
          : kinds.includes("business")
            ? "business"
            : "fixed";
    return { box, title, from: at.words.join(" · "), kind };
  });
}

/** The fixed boxes a setup holds, read and cleaned; anything missing takes the template's. */
export function readFixed(input: unknown, base?: ClaimFixed): ClaimFixed {
  const b = (input && typeof input === "object" ? input : {}) as Record<string, unknown>;
  const d: ClaimFixed = base ?? {
    carrier: ["", "", "", ""],
    box1: "Other",
    plan: "",
    box23: false,
    signature: "Signature on File",
    prefix: "",
    account: "{prefix}-WE{week_end}",
    instructions: true,
  };
  const str = (v: unknown, fallback: string, max = 80) =>
    typeof v === "string" ? v.replace(/\s+/g, " ").trim().slice(0, max) : fallback;
  const carrier = Array.isArray(b.carrier)
    ? [0, 1, 2, 3].map((i) => str((b.carrier as unknown[])[i], ""))
    : d.carrier;
  const box1 = str(b.box1, d.box1);
  return {
    carrier,
    box1: (CMS1500_STATES.insurance_type ?? []).includes(box1) ? box1 : d.box1,
    plan: str(b.plan, d.plan),
    box23: typeof b.box23 === "boolean" ? b.box23 : d.box23,
    signature: str(b.signature, d.signature, 40),
    prefix: str(b.prefix, d.prefix, 20).toUpperCase(),
    account: str(b.account, d.account, 60),
    instructions: typeof b.instructions === "boolean" ? b.instructions : d.instructions,
  };
}

/** Line rules, read: one of the three groupings, 1 to 6 lines to a form. */
export function readLineRules(input: unknown): LineRules {
  const b = (input && typeof input === "object" ? input : {}) as Record<string, unknown>;
  const group = ["date", "visit", "day"].includes(String(b.group))
    ? (b.group as LineRules["group"])
    : "date";
  const per = Math.floor(Number(b.perForm));
  return { group, perForm: per >= 1 && per <= CMS1500_LINES_PER_FORM ? per : 6 };
}

/** A service code as typed on the setup's Service codes: checked, or why not. */
export function readServiceCode(
  input: unknown,
): { ok: true; value: ServiceCode } | { ok: false; error: string } {
  const b = (input && typeof input === "object" ? input : {}) as Record<string, unknown>;
  const hcpcs = typeof b.hcpcs === "string" ? b.hcpcs.trim().toUpperCase() : "";
  if (hcpcs && !/^[A-Z0-9]{5}$/.test(hcpcs))
    return { ok: false, error: "A HCPCS or CPT code is 5 letters and numbers (S9122, 99347)." };
  const modifiers = (typeof b.modifiers === "string" ? b.modifiers : "")
    .toUpperCase()
    .split(/[\s,]+/)
    .filter(Boolean);
  if (modifiers.length > 4 || modifiers.some((m) => !/^[A-Z0-9]{2}$/.test(m)))
    return { ok: false, error: "Up to 4 modifiers of 2 letters or numbers each (U1 TT)." };
  const pos = typeof b.pos === "string" && b.pos.trim() ? b.pos.trim() : "12";
  if (!/^\d{2}$/.test(pos)) return { ok: false, error: "The place of service is 2 digits (12)." };
  const npi = b.npi === "billing" ? "billing" : "rendering";
  const group = ["date", "visit", "day"].includes(String(b.group))
    ? (b.group as LineRules["group"])
    : undefined;
  return {
    ok: true,
    value: { hcpcs, modifiers: modifiers.join(" "), pos, npi, ...(group ? { group } : {}) },
  };
}

/** What the setup's packet holds, in order (the claim always first), as data. */
export interface PacketPart {
  kind: "claim" | "authorization_letter" | "invoice" | "document";
  /** A document: the patient's latest file filed as this ("Physician orders"). */
  docType?: string;
}
export function readPacket(input: unknown): PacketPart[] {
  const list = Array.isArray(input) ? input : [];
  const out: PacketPart[] = [{ kind: "claim" }];
  for (const raw of list) {
    const p = (raw && typeof raw === "object" ? raw : {}) as Record<string, unknown>;
    if (p.kind === "authorization_letter" || p.kind === "invoice") {
      if (!out.some((x) => x.kind === p.kind)) out.push({ kind: p.kind });
    } else if (p.kind === "document" && typeof p.docType === "string" && p.docType.trim())
      out.push({ kind: "document", docType: p.docType.trim().slice(0, 80) });
  }
  return out.slice(0, 12);
}
export const packetWords = (p: PacketPart) =>
  p.kind === "claim"
    ? "CMS-1500 pages"
    : p.kind === "authorization_letter"
      ? "Authorization letter"
      : p.kind === "invoice"
        ? "Invoice"
        : (p.docType ?? "Document");

/**
 * The four setup steps (mockup): the accepted claim (optional, for reference: brief 1E-15), the
 * fixed boxes filled, every billed service's code set, every patient box mapped. No claim is made
 * until the last three are done.
 */
export function setupSteps(input: {
  sample: boolean;
  fixed: ClaimFixed;
  codes: readonly { hcpcs: string }[];
  sources: Readonly<BoxSources>;
}): { label: string; done: boolean }[] {
  const patientBoxes = [
    "insurance_id",
    "pt_name",
    "birth_mm",
    "ins_name",
    "pt_street",
    "ins_street",
    "cur_ill_mm",
  ];
  return [
    // Optional: never holds the setup back (mockup v65 shows it done).
    { label: "Sample (optional)", done: true },
    {
      label: "Fixed boxes filled",
      done: !!(
        input.fixed.carrier[0] &&
        input.fixed.carrier[1] &&
        input.fixed.carrier[2] &&
        input.fixed.signature
      ),
    },
    {
      label: "Service codes set",
      done: input.codes.length > 0 && input.codes.every((c) => !!c.hcpcs),
    },
    { label: "Patient boxes mapped", done: patientBoxes.every((f) => !!input.sources[f]) },
  ];
}

// ---- Charges rolled into the trip's line (brief 1E-16 addition) -----------------------------------

/** Where a setup's line goes on the claim: its own line, rolled into the trip's line, or none. */
export type ClaimLineHow = "own" | "trip" | "none";
export const CLAIM_LINE_LABEL: Record<ClaimLineHow, string> = {
  own: "Its own line",
  trip: "Rolled into the trip's line",
  none: "Not on the claim",
};

/**
 * The invoice lines that go on the claim. A charge rolled into the trip's line adds its amount to
 * the service line of the same trip or leg (visit), so the claim has one line per trip or leg per
 * date with the total; with no such line on the claim it keeps its own line. A charge kept off the
 * claim is left out. The invoice keeps every charge on its own line either way.
 */
export function rollIntoTrips<
  L extends { id: string; eventId: string; kind: string; amountCents: number },
>(lines: readonly L[], howOf: (l: L) => ClaimLineHow): L[] {
  const kept = lines.filter((l) => howOf(l) !== "none").map((l) => ({ ...l }));
  const out: L[] = [];
  const rolled: L[] = [];
  for (const l of kept) (howOf(l) === "trip" && l.kind !== "service" ? rolled : out).push(l);
  for (const l of rolled) {
    const trip = out.find((x) => x.eventId === l.eventId && x.kind === "service");
    if (trip) trip.amountCents += l.amountCents;
    else out.push(l);
  }
  return out;
}
